import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface AuthUser {
  id: number;
  username: string;
  email: string;
  firstname?: string;
  lastname?: string;
  avatar?: string | null;
  default_language?: string;
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  permissions: Set<string>;
  locale: string;
  rtl: boolean;
  /** Whether the backend is running in demo mode (drives the demo banner). */
  demoMode: boolean;

  setToken: (token: string | null) => void;
  setUser: (user: AuthUser | null) => void;
  setPermissions: (permissions: string[]) => void;
  setLocale: (locale: string, rtl?: boolean) => void;
  setDemoMode: (demoMode: boolean) => void;
  logout: () => void;
}

const STORAGE_KEY = 'elshahd-auth';

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      permissions: new Set<string>(),
      locale: 'ar',
      rtl: true,
      demoMode: false,

      setToken: (token) => set({ token }),
      setUser: (user) => set({ user }),
      setPermissions: (permissions) => set({ permissions: new Set(permissions) }),
      setLocale: (locale, rtl) =>
        set({ locale, rtl: rtl ?? (locale === 'ar' || locale === 'he' || locale === 'fa') }),
      setDemoMode: (demoMode) => set({ demoMode }),

      logout: () =>
        set({
          token: null,
          user: null,
          permissions: new Set<string>(),
        }),
    }),
    {
      name: STORAGE_KEY,
      // Set is not JSON-serialisable; (de)serialise manually.
      partialize: (s) => ({
        token: s.token,
        user: s.user,
        permissions: Array.from(s.permissions),
        locale: s.locale,
        rtl: s.rtl,
        demoMode: s.demoMode,
      }),
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<{
          token: string | null;
          user: AuthUser | null;
          permissions: string[];
          locale: string;
          rtl: boolean;
          demoMode: boolean;
        }>;
        return {
          ...current,
          token: p.token ?? null,
          user: p.user ?? null,
          permissions: new Set(p.permissions ?? []),
          locale: p.locale ?? current.locale,
          rtl: p.rtl ?? current.rtl,
          demoMode: p.demoMode ?? false,
        };
      },
    },
  ),
);
